import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ActionIcon, Badge, Box, Card, Flex, Group, Text } from '@mantine/core';
import { IconGripVertical, IconPencil, IconTrash } from '@tabler/icons-react';

const SlidePreview = ({ html }) => {
  if (!html) {
    return (
      <Box
        style={{
          width: 120,
          height: 80,
          borderRadius: 4,
          backgroundColor: 'var(--mantine-color-gray-1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text size="xs" c="dimmed">
          Empty
        </Text>
      </Box>
    );
  }

  return (
    <Box
      style={{
        width: 120,
        height: 80,
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid var(--mantine-color-gray-3)',
        flexShrink: 0,
      }}
    >
      <iframe
        title="Slide preview"
        srcDoc={html}
        style={{
          width: 360,
          height: 240,
          border: 'none',
          transform: 'scale(0.3333)',
          transformOrigin: '0 0',
          pointerEvents: 'none',
        }}
        tabIndex={-1}
      />
    </Box>
  );
};

const SortableSlideItem = ({ slide, index, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: slide._sortId || slide.id?.toString() || `new-${index}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card mb="xs" withBorder>
        <Flex gap="sm" align="center">
          <Box {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
            <IconGripVertical size={18} color="var(--mantine-color-dimmed)" />
          </Box>
          <SlidePreview html={slide.html} />
          <Box style={{ flex: 1 }}>
            <Flex align="center" gap="sm">
              <Text fw={500} size="sm">
                Slide {index + 1}
              </Text>
              {slide.durationSeconds && (
                <Badge size="xs" variant="light" color="gray">
                  {slide.durationSeconds}s
                </Badge>
              )}
            </Flex>
          </Box>
          <Group gap="xs">
            <ActionIcon variant="subtle" onClick={() => onEdit(index)}>
              <IconPencil size={16} />
            </ActionIcon>
            <ActionIcon variant="subtle" color="red" onClick={() => onDelete(index)}>
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        </Flex>
      </Card>
    </div>
  );
};

const StorySlideList = ({ slides, onEdit, onDelete, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const slidesWithSortIds = slides.map((slide, index) => ({
    ...slide,
    _sortId: slide.id?.toString() || `new-${index}`,
  }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = slidesWithSortIds.findIndex((s) => s._sortId === active.id);
    const newIndex = slidesWithSortIds.findIndex((s) => s._sortId === over.id);
    const reordered = arrayMove(slides, oldIndex, newIndex).map((s, i) => ({ ...s, position: i }));
    onReorder(reordered);
  };

  if (slides.length === 0) {
    return (
      <Text c="dimmed" ta="center" py="xl">
        No slides yet. Add your first slide to get started.
      </Text>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={slidesWithSortIds.map((s) => s._sortId)}
        strategy={verticalListSortingStrategy}
      >
        {slidesWithSortIds.map((slide, index) => (
          <SortableSlideItem
            key={slide._sortId}
            slide={slide}
            index={index}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default StorySlideList;
